import { localized, t } from '@weavedev/lit-i18next';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { createRef, ref, Ref } from 'lit/directives/ref.js';

import {
  FilterOptionLayout,
  PrintOptionCollapsibleFilterOptionDto,
} from '@/api-sdk';
import {
  iconCheckmark,
  iconFullColor,
  iconStopwatch,
} from '@/components/icons';
import { MobileController } from '@/controllers';
import { baseStyles, buttonStyles, collapsibleOptionsStyles } from '@/styles';

import '@/components/fullscreen-image/fg-fullscreen-image';
import '@/components/tooltip/fg-tooltip';

@customElement('fg-collapsible-filter-item')
@localized()
export class FgCollapsibleFilterItem extends LitElement {
  @property({ type: Number })
  index = 0;

  @property({ type: Number })
  subStepIndex = 0;

  @property({ type: Number })
  filterIndex = 0;

  @property({ type: Number })
  filterOptionIndex = 0;

  @property({ type: Boolean })
  focussed = false;

  @property({ type: Object })
  option?: PrintOptionCollapsibleFilterOptionDto;

  @property({ type: String })
  filterOptionLayout?: FilterOptionLayout;

  private tooltipBoundaryRef: Ref<HTMLElement> = createRef();
  private buttonRef: Ref<HTMLButtonElement> = createRef();

  protected mobileController = new MobileController(this);

  updateComponent(queryString: string | null | undefined) {
    if (queryString === null || queryString === undefined) {
      console.error('Missing query string');
      return;
    }
    this.dispatchEvent(
      new CustomEvent('option-selected', {
        composed: true,
        detail: {
          option: {
            queryString,
            filterOptionLayout: this.filterOptionLayout,
            code: this.option?.code,
            subStepIndex: this.subStepIndex,
          },
        },
      }),
    );
  }

  render() {
    if (this.option === null || this.option === undefined) return nothing;

    const { isMobile } = this.mobileController;

    this.updateComplete.then(() => {
      /**
       * We need to check if we are currently on mobile and prevent the default focus state when the step is open and
       * active. Because the focus state wil ignore the mobile configurator current step location and force the focused
       * step in view and this breaks the configurator forward and back buttons. This check could be removed when the
       * mobile configurator is rebuild with the new configurator steps.
       */
      if (this.filterOptionIndex === 0 && this.focussed && !isMobile) {
        requestAnimationFrame(() => {
          this.buttonRef.value?.focus();
        });
      }
    });

    this.tooltipBoundaryRef = createRef();
    const wrapperRef = ref(this.tooltipBoundaryRef);
    const buttonRef = ref(this.buttonRef);

    return html`
      <li
        class="wrapper ${classMap({
          selected: this.option.selected,
          [`filter-option-layout-${this.filterOptionLayout}`]:
            this.filterOptionLayout != null,
        })}"
      >
        <button
          name="option_${this.filterOptionLayout}${this.index}_${this
            .filterIndex}"
          id="${this.filterOptionLayout}${this.index}_${this.filterIndex}_${this
            .filterOptionIndex}"
          @click=${() => this.updateComponent(this.option?.queryString)}
          ${buttonRef}
          class="option-button"
        ></button>
        <span ${wrapperRef}>
          ${this.option.mostChosen
            ? html`
                <span class="most-chosen">${t('labels.mostChosen')}</span>
              `
            : nothing}
          <label
            for="${this.filterOptionLayout}${this.index}_${this
              .filterIndex}_${this.filterOptionIndex}"
            class="printoption"
            tabindex="-1"
          >
            <span
              aria-hidden="true"
              class="checkmark ${classMap({
                selected: this.option.selected,
              })}"
            >
              ${iconCheckmark}
            </span>
            ${this.option.image?.thumbnailUrl
              ? html`
                  <span class="image-container">
                    <span class="image-container-inner">
                      <img src=${this.option.image?.thumbnailUrl} alt="" />
                    </span>
                  </span>
                `
              : nothing}

            <span class="filter-option-content ">
              <span class="name">
                ${this.option.code === 'fullColor'
                  ? iconFullColor
                  : nothing}${this.option.name}
              </span>
              ${this.option.description
                ? html`
                    <dl>
                      <dt>${this.option.description?.label}</dt>
                      <dd>${this.option.description?.value}</dd>
                    </dl>
                  `
                : nothing}
              ${this.option.hspAvailable
                ? html`
                    <span class="production-speed" aria-hidden="true">
                      ${iconStopwatch}
                      ${t('content.productionSpeed.quickDelivery')}
                    </span>
                  `
                : nothing}
            </span>
          </label>
          ${this.option.explanation
            ? html`
                <span class="more-info-wrapper">
                  <span>${t('actions.moreInfo')}</span>
                  <fg-tooltip
                    .parentRef=${this.tooltipBoundaryRef}
                    .tooltipData=${this.option.explanation}
                  ></fg-tooltip>
                </span>
              `
            : nothing}
        </span>
        ${this.option.image?.mainUrl && this.option.image?.mainUrl !== ''
          ? html`
              <fg-fullscreen-image
                .imageUrl=${this.option.image?.mainUrl}
                hasZoomInCursor
              ></fg-fullscreen-image>
            `
          : nothing}
      </li>
    `;
  }

  static styles = [
    baseStyles,
    buttonStyles,
    collapsibleOptionsStyles,
    css`
      li.wrapper {
        background-color: #fff;
        border: 1px solid var(--fg-gray--300, var(--fg-fallback-gray--300));
        border-radius: var(--fg-borderradius, var(--fg-fallback-borderradius));
        height: 100%;
        position: relative;

        &:hover {
          border: 1px solid var(--fg-gray--500, var(--fg-fallback-gray--500));

          label.printoption .name {
            text-decoration: underline;
          }
        }
      }
      li.wrapper.selected {
        background-color: var(
          --fg-message-success--light,
          var(--fg-fallback-message-success--light)
        );
        border: 2px solid
          var(--fg-message-success, var(--fg-fallback-message-success));
        position: relative;
      }
      li.filter-option-layout-image > span {
        align-items: center;
        display: flex;
        height: 100%;
        justify-content: space-between;
        width: 100%;
      }
      li.filter-option-layout-default > span {
        align-items: center;
        display: flex;
        justify-content: space-between;
        width: 100%;
      }
      .option-button {
        appearance: none;
        border-radius: var(--fg-borderradius, var(--fg-fallback-borderradius));
        height: 100%;
        left: 0;
        margin: 0;
        padding: 0;
        position: absolute;
        top: 0;
        width: 100%;
      }

      label.printoption {
        cursor: pointer;
        display: flex;
        flex-direction: column;
        height: 100%;
        min-height: 64px;
        min-width: 64px;
        padding: 16px;
        width: 100%;
      }

      span.most-chosen {
        background-color: var(
          --fg-message-success--light,
          var(--fg-fallback-message-success--light)
        );
        border-radius: var(
          --fg-borderradius--lg,
          var(--fg-fallback-borderradius--lg)
        );
        color: var(--fg-message-success, var(--fg-fallback-message-success));
        display: block;
        font-size: 0.875rem;
        font-weight: 600;
        height: 24px;
        line-height: 140%;
        padding: 2px 8px;
        position: absolute;
        right: -3px;
        top: -12px;
        white-space: nowrap;
      }

      .image-container {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 100%;
      }

      .image-container-inner {
        margin-bottom: 12px;
        height: 124px;
        width: 124px;
      }
      .image-container-inner img {
        width: 100%;
        height: 100%;
        object-fit: contain;
      }
      fg-fullscreen-image {
        visibility: hidden;
      }
      li.wrapper:hover fg-fullscreen-image,
      li.wrapper:focus-within fg-fullscreen-image {
        visibility: visible;
      }
      fg-fullscreen-image {
        position: absolute;
        top: 16px;
        right: 16px;
      }
      li.filter-option-layout-image button {
        align-items: center;
      }
      button {
        display: flex;
        flex-direction: column;
        width: 100%;
      }
      .production-speed {
        align-items: center;
        display: flex;
        gap: 4px;
        line-height: 100%;
      }

      .name {
        display: flex;
        gap: 4px;
        font-size: 1rem;
        font-weight: 700;
        line-height: 1rem;
        text-align: center;
      }

      dl,
      dd {
        column-gap: 4px;
        display: flex;
        flex-wrap: wrap;
        margin: 0;
      }
      dd {
        font-weight: 600;
      }
      dd,
      dt {
        text-align: center;
      }
      li.filter-option-layout-image dl,
      li.filter-option-layout-image dd {
        justify-content: center;
      }
      .filter-option-content {
        align-items: center;
        display: flex;
        flex-direction: column;
        flex-grow: 1;
        gap: 8px;
        justify-content: center;
      }
      li.filter-option-layout-image .filter-option-content {
        justify-content: end;
      }
      li.filter-option-layout-colors .filter-option-content {
        justify-content: center;
      }
      li.filter-option-layout-default .filter-option-content {
        align-items: start;
        && .name {
          text-align: left;
        }
      }
      .more-info-wrapper {
        align-items: center;
        color: var(--fg-primary, var(--fg-fallback-primary));
        display: flex;
        flex-wrap: nowrap;
        gap: 4px;
        justify-content: end;
        font-size: 0.875rem;
        padding-right: 16px;
      }

      .more-info-wrapper > span {
        width: max-content;
        z-index: 10;
      }

      fg-tooltip {
        color: var(--fg-primary, var(--fg-fallback-primary));
        display: flex;
        z-index: 20;
      }
    `,
  ];
}
